import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Link,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import { useGlobalConfig } from 'context/GlobalContext';
import { PilotApis } from 'apis/pilotAPI';
import { Environment } from 'types/apis/pilotInterfaces';
import { BglPrimaryButton, BglPrimaryOutlinedButton } from 'components/button/BglButtons';

export const EnvironmentListPage = () => {
  const { rc } = useGlobalConfig();
  const [environments, setEnvironments] = useState<Environment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState<number>(0);

  // Fetch all environments on component mount
  useEffect(() => {
    fetchEnvironments();
  }, []);

  const fetchEnvironments = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await PilotApis.getAllEnvironments(rc);
      
      if (response.error) {
        setError('Failed to fetch environments');
        console.error('Error fetching environments:', response.error);
        // Use mock data for testing when API fails
        setEnvironments([
          {
            id: 1,
            environment_name: 'uat',
            status: 'active',
            stack_name: 'efficiency-manitoba',
            utility_id: 1,
            utility: {
              id: 1,
              name: 'Efficiency Manitoba',
              created_at: '2025-07-29T18:11:52 GMT',
              updated_at: '2025-07-29T18:11:52 GMT'
            },
            process_id: '466e1552-f4b9-4310-9e3b-b8b07a9b31c5',
            process_name: 'Sub-Environment: uat',
            process_type: 'sub_environment_creation',
            task_id: 'ac39e034-8f8a-4a06-be76-b030b585241a',
            created_at: '2025-08-07T17:51:23 GMT',
            updated_at: '2025-08-08T15:30:10 GMT',
            process_data: {}
          },
          {
            id: 2,
            environment_name: 'dev',
            status: 'active',
            stack_name: 'bc-hydro',
            utility_id: 2,
            utility: {
              id: 2,
              name: 'BC Hydro',
              created_at: '2025-07-29T18:11:52 GMT',
              updated_at: '2025-07-29T18:11:52 GMT'
            },
            process_id: '566e1552-f4b9-4310-9e3b-b8b07a9b31c6',
            process_name: 'Sub-Environment: dev',
            process_type: 'sub_environment_creation',
            task_id: 'bc39e034-8f8a-4a06-be76-b030b585241b',
            created_at: '2025-08-07T17:51:23 GMT',
            updated_at: '2025-08-08T15:30:10 GMT',
            process_data: {}
          },
          {
            id: 3,
            environment_name: 'nonprodqa',
            status: 'active',
            stack_name: 'ca-qa',
            utility_id: 3,
            utility: {
              id: 3,
              name: 'CA QA',
              created_at: '2025-07-29T18:11:52 GMT',
              updated_at: '2025-07-29T18:11:52 GMT'
            },
            process_id: '666e1552-f4b9-4310-9e3b-b8b07a9b31c7',
            process_name: 'Sub-Environment: nonprodqa',
            process_type: 'sub_environment_creation',
            task_id: 'cc39e034-8f8a-4a06-be76-b030b585241c',
            created_at: '2025-08-07T17:51:23 GMT',
            updated_at: '2025-08-08T15:30:10 GMT',
            process_data: {}
          },
          {
            id: 4,
            environment_name: 'prod',
            status: 'active',
            stack_name: 'ca-prod',
            utility_id: 4,
            utility: {
              id: 4,
              name: 'CA Production',
              created_at: '2025-07-29T18:11:52 GMT',
              updated_at: '2025-07-29T18:11:52 GMT'
            },
            process_id: '766e1552-f4b9-4310-9e3b-b8b07a9b31c8',
            process_name: 'Sub-Environment: prod',
            process_type: 'sub_environment_creation',
            task_id: 'dc39e034-8f8a-4a06-be76-b030b585241d',
            created_at: '2025-08-07T17:51:23 GMT',
            updated_at: '2025-08-08T15:30:10 GMT',
            process_data: {}
          },
          {
            id: 5,
            environment_name: 'ds',
            status: 'active',
            stack_name: 'generic-utility',
            utility_id: 5,
            utility: {
              id: 5,
              name: 'Generic Utility',
              created_at: '2025-07-29T18:11:52 GMT',
              updated_at: '2025-07-29T18:11:52 GMT'
            },
            process_id: '866e1552-f4b9-4310-9e3b-b8b07a9b31c9',
            process_name: 'Sub-Environment: ds',
            process_type: 'sub_environment_creation',
            task_id: 'ec39e034-8f8a-4a06-be76-b030b585241e',
            created_at: '2025-08-07T17:51:23 GMT',
            updated_at: '2025-08-08T15:30:10 GMT',
            process_data: {}
          }
        ]);
        setTotalCount(5);
      } else {
        const environmentsData = response.data?.data || [];
        const count = response.data?.count || 0;
        setTotalCount(count);
        
        if (environmentsData.length === 0) {
          // Use mock data if API returns empty array
          setEnvironments([
            {
              id: 1,
              environment_name: 'uat',
              status: 'active',
              stack_name: 'efficiency-manitoba',
              utility_id: 1,
              utility: {
                id: 1,
                name: 'Efficiency Manitoba',
                created_at: '2025-07-29T18:11:52 GMT',
                updated_at: '2025-07-29T18:11:52 GMT'
              },
              process_id: '466e1552-f4b9-4310-9e3b-b8b07a9b31c5',
              process_name: 'Sub-Environment: uat',
              process_type: 'sub_environment_creation',
              task_id: 'ac39e034-8f8a-4a06-be76-b030b585241a',
              created_at: '2025-08-07T17:51:23 GMT',
              updated_at: '2025-08-08T15:30:10 GMT',
              process_data: {}
            },
            {
              id: 2,
              environment_name: 'dev',
              status: 'active',
              stack_name: 'bc-hydro',
              utility_id: 2,
              utility: {
                id: 2,
                name: 'BC Hydro',
                created_at: '2025-07-29T18:11:52 GMT',
                updated_at: '2025-07-29T18:11:52 GMT'
              },
              process_id: '566e1552-f4b9-4310-9e3b-b8b07a9b31c6',
              process_name: 'Sub-Environment: dev',
              process_type: 'sub_environment_creation',
              task_id: 'bc39e034-8f8a-4a06-be76-b030b585241b',
              created_at: '2025-08-07T17:51:23 GMT',
              updated_at: '2025-08-08T15:30:10 GMT',
              process_data: {}
            },
            {
              id: 3,
              environment_name: 'nonprodqa',
              status: 'active',
              stack_name: 'ca-qa',
              utility_id: 3,
              utility: {
                id: 3,
                name: 'CA QA',
                created_at: '2025-07-29T18:11:52 GMT',
                updated_at: '2025-07-29T18:11:52 GMT'
              },
              process_id: '666e1552-f4b9-4310-9e3b-b8b07a9b31c7',
              process_name: 'Sub-Environment: nonprodqa',
              process_type: 'sub_environment_creation',
              task_id: 'cc39e034-8f8a-4a06-be76-b030b585241c',
              created_at: '2025-08-07T17:51:23 GMT',
              updated_at: '2025-08-08T15:30:10 GMT',
              process_data: {}
            },
            {
              id: 4,
              environment_name: 'prod',
              status: 'active',
              stack_name: 'ca-prod',
              utility_id: 4,
              utility: {
                id: 4,
                name: 'CA Production',
                created_at: '2025-07-29T18:11:52 GMT',
                updated_at: '2025-07-29T18:11:52 GMT'
              },
              process_id: '766e1552-f4b9-4310-9e3b-b8b07a9b31c8',
              process_name: 'Sub-Environment: prod',
              process_type: 'sub_environment_creation',
              task_id: 'dc39e034-8f8a-4a06-be76-b030b585241d',
              created_at: '2025-08-07T17:51:23 GMT',
              updated_at: '2025-08-08T15:30:10 GMT',
              process_data: {}
            },
            {
              id: 5,
              environment_name: 'ds',
              status: 'active',
              stack_name: 'generic-utility',
              utility_id: 5,
              utility: {
                id: 5,
                name: 'Generic Utility',
                created_at: '2025-07-29T18:11:52 GMT',
                updated_at: '2025-07-29T18:11:52 GMT'
              },
              process_id: '866e1552-f4b9-4310-9e3b-b8b07a9b31c9',
              process_name: 'Sub-Environment: ds',
              process_type: 'sub_environment_creation',
              task_id: 'ec39e034-8f8a-4a06-be76-b030b585241e',
              created_at: '2025-08-07T17:51:23 GMT',
              updated_at: '2025-08-08T15:30:10 GMT',
              process_data: {}
            }
          ]);
          setTotalCount(5);
        } else {
          setEnvironments(environmentsData);
        }
      }
    } catch (err) {
      setError('An error occurred while fetching environments');
      console.error('Exception fetching environments:', err);
      // Use mock data for testing when exception occurs
      setEnvironments([
        {
          id: 1,
          environment_name: 'uat',
          status: 'active',
          stack_name: 'efficiency-manitoba',
          utility_id: 1,
          utility: {
            id: 1,
            name: 'Efficiency Manitoba',
            created_at: '2025-07-29T18:11:52 GMT',
            updated_at: '2025-07-29T18:11:52 GMT'
          },
          process_id: '466e1552-f4b9-4310-9e3b-b8b07a9b31c5',
          process_name: 'Sub-Environment: uat',
          process_type: 'sub_environment_creation',
          task_id: 'ac39e034-8f8a-4a06-be76-b030b585241a',
          created_at: '2025-08-07T17:51:23 GMT',
          updated_at: '2025-08-08T15:30:10 GMT',
          process_data: {}
        },
        {
          id: 2,
          environment_name: 'dev',
          status: 'active',
          stack_name: 'bc-hydro',
          utility_id: 2,
          utility: {
            id: 2,
            name: 'BC Hydro',
            created_at: '2025-07-29T18:11:52 GMT',
            updated_at: '2025-07-29T18:11:52 GMT'
          },
          process_id: '566e1552-f4b9-4310-9e3b-b8b07a9b31c6',
          process_name: 'Sub-Environment: dev',
          process_type: 'sub_environment_creation',
          task_id: 'bc39e034-8f8a-4a06-be76-b030b585241b',
          created_at: '2025-08-07T17:51:23 GMT',
          updated_at: '2025-08-08T15:30:10 GMT',
          process_data: {}
        },
        {
          id: 3,
          environment_name: 'nonprodqa',
          status: 'active',
          stack_name: 'ca-qa',
          utility_id: 3,
          utility: {
            id: 3,
            name: 'CA QA',
            created_at: '2025-07-29T18:11:52 GMT',
            updated_at: '2025-07-29T18:11:52 GMT'
          },
          process_id: '666e1552-f4b9-4310-9e3b-b8b07a9b31c7',
          process_name: 'Sub-Environment: nonprodqa',
          process_type: 'sub_environment_creation',
          task_id: 'cc39e034-8f8a-4a06-be76-b030b585241c',
          created_at: '2025-08-07T17:51:23 GMT',
          updated_at: '2025-08-08T15:30:10 GMT',
          process_data: {}
        },
        {
          id: 4,
          environment_name: 'prod',
          status: 'active',
          stack_name: 'ca-prod',
          utility_id: 4,
          utility: {
            id: 4,
            name: 'CA Production',
            created_at: '2025-07-29T18:11:52 GMT',
            updated_at: '2025-07-29T18:11:52 GMT'
          },
          process_id: '766e1552-f4b9-4310-9e3b-b8b07a9b31c8',
          process_name: 'Sub-Environment: prod',
          process_type: 'sub_environment_creation',
          task_id: 'dc39e034-8f8a-4a06-be76-b030b585241d',
          created_at: '2025-08-07T17:51:23 GMT',
          updated_at: '2025-08-08T15:30:10 GMT',
          process_data: {}
        },
        {
          id: 5,
          environment_name: 'ds',
          status: 'active',
          stack_name: 'generic-utility',
          utility_id: 5,
          utility: {
            id: 5,
            name: 'Generic Utility',
            created_at: '2025-07-29T18:11:52 GMT',
            updated_at: '2025-07-29T18:11:52 GMT'
          },
          process_id: '866e1552-f4b9-4310-9e3b-b8b07a9b31c9',
          process_name: 'Sub-Environment: ds',
          process_type: 'sub_environment_creation',
          task_id: 'ec39e034-8f8a-4a06-be76-b030b585241e',
          created_at: '2025-08-07T17:51:23 GMT',
          updated_at: '2025-08-08T15:30:10 GMT',
          process_data: {}
        }
      ]);
      setTotalCount(5);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewEnvironment = () => {
    // TODO: Implement new environment creation
    console.log('New Environment clicked');
  };

  const handleExportJSON = () => {
    try {
      const dataStr = JSON.stringify(environments, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'environments.json';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting JSON:', err);
    }
  };

  const handleViewEnvironment = (environment: Environment) => {
    // TODO: Implement view environment details
    console.log('View environment:', environment);
  };

  const handleEditEnvironment = (environment: Environment) => {
    // TODO: Implement edit environment
    console.log('Edit environment:', environment);
  };

  const handleDeleteEnvironment = (environment: Environment) => {
    // TODO: Implement delete environment with confirmation
    console.log('Delete environment:', environment);
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'success';
      case 'completed':
        return 'success';
      case 'inactive':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button onClick={fetchEnvironments} variant="contained">
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Environments Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden', boxShadow: 2, borderRadius: 2, p: 4 }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center',justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <LinkIcon sx={{ mr: 2, color: 'primary.main', fontSize: 32 }} />
            <Typography variant="h4" component="h1" sx={{ 
              fontWeight: 700, 
              color: 'text.primary',
              flex: 1
            }}>
              Environments
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <BglPrimaryOutlinedButton
              onClick={handleExportJSON}
              startIcon={<DownloadIcon />}
            >
              Export JSON
            </BglPrimaryOutlinedButton>
            <BglPrimaryButton
              onClick={handleNewEnvironment}
              startIcon={<AddIcon />}
            >
              New Environment
            </BglPrimaryButton>
          </Box>
        </Box>
        <Typography variant="body1" sx={{ 
          color: 'text.secondary',
          fontSize: '1rem',
          textAlign: 'left'
        }}>
          Manage and view different deployment environments. {totalCount > 0 && `Total: ${totalCount} environments`}
        </Typography>
      </Box>

        <Box>
        <TableContainer style={{border: '1px solid rgb(222, 222, 222)',borderRadius: '6px'}}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  backgroundColor: 'background.paper',
                  borderBottom: '2px solid',
                  borderColor: 'divider'
                }}>
                  Name
                </TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  backgroundColor: 'background.paper',
                  borderBottom: '2px solid',
                  borderColor: 'divider'
                }}>
                  Utility
                </TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  backgroundColor: 'background.paper',
                  borderBottom: '2px solid',
                  borderColor: 'divider'
                }}>
                  Server URL
                </TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  backgroundColor: 'background.paper',
                  borderBottom: '2px solid',
                  borderColor: 'divider'
                }}>
                  Status
                </TableCell>
                <TableCell sx={{ 
                  fontWeight: 600, 
                  backgroundColor: 'background.paper',
                  borderBottom: '2px solid',
                  borderColor: 'divider',
                  textAlign: 'center'
                }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {environments.map((environment) => (
                <TableRow key={environment.id} hover>
                  <TableCell sx={{ fontWeight: 500, color: 'text.primary' }}>
                    {environment.environment_name}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {environment.utility?.name || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {`https://api-server-${environment.stack_name}-uat.bidgely.com`} 
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={environment.status} 
                      color={getStatusColor(environment.status)}
                      size="small"
                      sx={{ fontWeight: 500 }}
                    />
                  </TableCell>
                  {/* <TableCell>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {environment.process_type}
                    </Typography>
                  </TableCell> */}
                  <TableCell sx={{ textAlign: 'center' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                      {/* <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleViewEnvironment(environment)}
                          sx={{ color: 'info.main' }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip> */}
                      <Tooltip title="Edit Environment">
                        <IconButton
                          size="small"
                          onClick={() => handleEditEnvironment(environment)}
                          sx={{ color: 'primary.main' }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {/* <Tooltip title="Delete Environment">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteEnvironment(environment)}
                          sx={{ color: 'error.main' }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip> */}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        {environments.length === 0 && (
          <Box sx={{ 
            p: 4, 
            textAlign: 'center',
            color: 'text.secondary'
          }}>
            <Typography variant="body1">
              No environments found. Create your first environment to get started.
            </Typography>
          </Box>
        )}
        </Box>
      </Paper>
    </Box>
  );
};